import { useState, type ReactNode } from 'react'
import { Avatar, Button, Dropdown, ListBox, Select } from '@heroui/react'
import type { ConnectionStatus } from '../lib/live'
import { homeTiles, type Tile, type TileKey } from '../lib/homeTiles'
import type { Branch, Identity, Staff } from '../lib/types'
import { ConnectionBadge } from './domain'
import { Icon, type IconName } from './icons'
import { LanguageMenu } from './LanguageMenu'
import { OrganisationSelect } from './OrgSwitch'
import { Logo } from './Logo'
import { useSession } from './Session'
import { sectionsFor } from '../pages/Administration'
import { initials } from '../lib/initials'
import { roleLabel } from '../lib/roleNames'
import type { StaffKey } from '../lib/staffText'
import { useStaffText } from './StaffText'
import { readTheme, saveTheme, type Theme } from './theme'
import { readRailCollapsed, saveRailCollapsed } from './rail'

/** Each page a person may open, as the rail names and draws it (the same order as Home's tiles). */
const FACES: Record<TileKey, { name: StaffKey; icon: IconName }> = {
  counter: { name: 'entry.app.counter', icon: 'counter' },
  reception: { name: 'entry.app.reception', icon: 'reception' },
  bookings: { name: 'entry.app.appointments', icon: 'booking' },
  floor: { name: 'entry.card.floor', icon: 'floor' },
  reports: { name: 'entry.app.reports', icon: 'reports' },
  audit: { name: 'entry.tile.audit', icon: 'audit' },
  signage: { name: 'entry.app.signage', icon: 'play' },
  designs: { name: 'entry.app.designs', icon: 'designs' },
  devices: { name: 'entry.card.devices', icon: 'machine' },
  people: { name: 'entry.app.people', icon: 'people' },
  administration: { name: 'entry.card.administration', icon: 'admin' },
  organisations: { name: 'admin.section.organisations', icon: 'organisations' },
}

/** The module rail is for the owner, Administrator, Branch manager, Supervisor and a role of the organisation's own that may change settings. */
export function keepsRail(identity: Identity): boolean {
  const { staff } = identity
  if (staff.owner || staff.roleKey === 'admin' || staff.roleKey === 'branchManager' || staff.roleKey === 'supervisor') return true
  return !staff.roleKey && sectionsFor(identity).length > 0
}

/** The tile the address is on: the exact address with its hash first, then the page alone. */
export function currentTile(tiles: readonly Tile[], path: string, hash: string): Tile | undefined {
  return tiles.find((t) => t.to === path + hash) ?? tiles.find((t) => t.to.split('#')[0] === path && !t.to.includes('#'))
}

/**
 * The rail on every staff screen (HeroUI): the logo (the way back to the main menu),
 * the organisation and branch, every page the person may open, and at the foot the
 * live link, the theme and who is signed in. Kept under its old name, Topbar, so a page
 * moves to the rail without its call sites changing. The language menu draws nothing
 * while App's ENGLISH_ONLY holds.
 */
export function Topbar({
  title,
  branch,
  branches = [],
  status,
  onChooseBranch,
}: {
  title?: string
  branch?: Branch | null
  branches?: Branch[]
  status?: ConnectionStatus
  onChooseBranch?: (id: string) => void
}) {
  const { lang, say } = useStaffText()
  const session = useSession()
  const [theme, setTheme] = useState<Theme>(readTheme)
  const [collapsed, setCollapsed] = useState<boolean>(readRailCollapsed)
  const toggleRail = () => {
    saveRailCollapsed(!collapsed)
    setCollapsed(!collapsed)
  }

  const flip = () => {
    const next = theme === 'light' ? 'dark' : 'light'
    saveTheme(next)
    setTheme(next)
  }

  const identity = session?.identity
  const staff = identity?.staff
  const org = identity?.organisation ?? say('entry.productName')
  const role = staff ? (staff.owner ? say('common.role.superadmin') : roleLabel(staff.roleKey, staff.roleName, lang)) : ''
  const path = typeof window === 'undefined' ? '/' : window.location.pathname
  const hash = typeof window === 'undefined' ? '' : window.location.hash
  const tiles = identity
    ? homeTiles({ permissions: identity.permissions, modules: identity.modules, administer: sectionsFor(identity).length > 0 })
    : []
  const current = currentTile(tiles, path, hash)

  const go = (to: string) => session?.navigate(to)
  const branchPicker = !session?.org?.needed && branches.length > 1 && !!onChooseBranch
  const picks = Boolean(session?.org?.switchable) || branchPicker

  const branchPart = session?.org?.needed ? null : branchPicker && onChooseBranch ? (
    <Select
      aria-label={say('common.branch')}
      variant="secondary"
      fullWidth
      value={branch?.id ?? null}
      onChange={(value) => value != null && onChooseBranch(String(value))}
    >
      <Select.Trigger>
        <Select.Value />
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover>
        <ListBox>
          {branches.map((b) => (
            <ListBox.Item key={b.id} id={b.id} textValue={b.name}>
              {b.name}
              <ListBox.ItemIndicator />
            </ListBox.Item>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  ) : (
    branch && <span className="rail__branch">{branch.name}</span>
  )
  const items = staff && session && (
    <Dropdown.Menu
      onAction={(key) => {
        if (key === 'account') session.navigate('/account')
        if (key === 'signout') session.signOut()
      }}
    >
      {staff.signIn !== 'directory' ? (
        <Dropdown.Item id="account" textValue={say('entry.account')}>
          {say('entry.account')}
        </Dropdown.Item>
      ) : null}
      <Dropdown.Item id="signout" textValue={say('common.signOut')}>
        {say('common.signOut')}
      </Dropdown.Item>
    </Dropdown.Menu>
  )
  const accountMenu = (above: boolean) =>
    staff && session && (
      <Dropdown>
        {/* The Button is the trigger; Dropdown.Trigger would wrap it in a second button. */}
        <Button variant="ghost" className="rail__account" aria-label={say('ui.accountMenu')}>
          <Avatar size="sm" color="accent">
            <Avatar.Fallback>{initials(staff.displayName, staff.username)}</Avatar.Fallback>
          </Avatar>
          <span className="rail__who">
            <span className="rail__name">{staff.displayName}</span>
            <span className="rail__role">{role}</span>
          </span>
        </Button>
        {above ? (
          <Dropdown.Popover placement="top start">{items}</Dropdown.Popover>
        ) : (
          <Dropdown.Popover placement="bottom end">{items}</Dropdown.Popover>
        )}
      </Dropdown>
    )
  const themeButton = (
    <Button
      isIconOnly
      variant="ghost"
      size="sm"
      onPress={flip}
      aria-label={theme === 'light' ? say('ui.themeDark') : say('ui.themeLight')}
    >
      <Icon name={theme === 'light' ? 'moon' : 'sun'} size={16} />
    </Button>
  )

  // Counter, Reception and report-only people get one slim bar: their few pages
  // are on Home, behind the logo. The module rail is for those who run the branch.
  if (identity && !keepsRail(identity)) {
    return (
      <header className="topbar" aria-label={say('ui.mainMenu')}>
        <button type="button" className="rail__home" onClick={() => go('/')} aria-label={say('ui.mainMenu')} title={say('ui.mainMenu')}>
          <Logo />
        </button>
        {branchPicker ? <div className="topbar__branch">{branchPart}</div> : branchPart}
        <span className="topbar__grow" />
        {status && <ConnectionBadge status={status} />}
        <LanguageMenu />
        {themeButton}
        {accountMenu(false)}
      </header>
    )
  }

  return (
    <aside className="rail" data-collapsed={collapsed || undefined} aria-label={say('ui.mainMenu')}>
      <div className="rail__top">
        <div className="rail__brand">
          <button type="button" className="rail__home" onClick={() => go('/')} aria-label={say('ui.mainMenu')} title={say('ui.mainMenu')}>
            {collapsed ? <span className="rail__mark" aria-hidden="true">Q</span> : <Logo />}
          </button>
          <Button
            isIconOnly
            variant="ghost"
            size="sm"
            className="rail__toggle"
            onPress={toggleRail}
            aria-label={collapsed ? say('ui.railExpand') : say('ui.railCollapse')}
            aria-expanded={!collapsed}
          >
            <Icon name={collapsed ? 'right' : 'left'} size={16} />
          </Button>
          {/* Collapsed, the pickers are hidden: this opens the rail on them. */}
          {collapsed && picks && (
            <Button
              isIconOnly
              variant="ghost"
              size="sm"
              className="rail__toggle"
              onPress={toggleRail}
              aria-label={branchPicker ? say('common.branch') : say('common.organisation')}
            >
              <Icon name={branchPicker ? 'pin' : 'organisations'} size={16} />
            </Button>
          )}
        </div>
        <div className="rail__ctx">
          {session?.org?.switchable ? <OrganisationSelect inBar /> : <span className="rail__org">{org}</span>}
          {/* With "All organisations" a branch belongs to nobody in view: show none. */}
          {branchPart}
          {title && !current && <span className="rail__title">{title}</span>}
        </div>
      </div>

      {session && (
        <nav className="rail__nav" aria-label={say('ui.allModules')}>
          <button type="button" className="rail__item" data-current={path === '/' || undefined} onClick={() => go('/')} title={collapsed ? say('ui.home') : undefined}>
            <Icon name="grid" size={16} />
            <span className="rail__label">{say('ui.home')}</span>
          </button>
          {tiles.map((tile) => (
            <button
              key={tile.key}
              type="button"
              className="rail__item"
              data-current={current?.key === tile.key || undefined}
              aria-current={current?.key === tile.key ? 'page' : undefined}
              onClick={() => go(tile.to)}
              title={collapsed ? say(FACES[tile.key].name) : undefined}
            >
              <Icon name={FACES[tile.key].icon} size={16} />
              <span className="rail__label">{say(FACES[tile.key].name)}</span>
            </button>
          ))}
        </nav>
      )}

      <div className="rail__foot">
        {status && (
          <span className="rail__state">
            <ConnectionBadge status={status} />
          </span>
        )}
        <LanguageMenu />
        <div className="rail__me">
          {accountMenu(true)}
          {themeButton}
        </div>
      </div>
    </aside>
  )
}

/**
 * The frame of a staff screen: the rail and the page beside it.
 *
 * Kept with the props it always had (staff, onNavigate, onSignOut), which the
 * rail now takes from the session instead, so a page moves to the new design
 * without its call sites changing.
 */
export function Chrome({
  title,
  branch,
  branches,
  status,
  onChooseBranch,
  setup,
  children,
}: {
  title?: string
  staff?: Staff
  branch?: Branch | null
  branches?: Branch[]
  status?: ConnectionStatus
  onChooseBranch?: (id: string) => void
  onNavigate?: (to: string) => void
  onSignOut?: () => void
  /** A page of setup: on a branch server it says where setup is changed. */
  setup?: boolean
  children: ReactNode
}) {
  return (
    <div className="app">
      <Topbar title={title} branch={branch} branches={branches} status={status} onChooseBranch={onChooseBranch} />
      <div className="app__main">
        {setup && <SetupAtNote />}
        {children}
      </div>
    </div>
  )
}

/** On a branch server (edge), one line: setup is read-only here and changed at its server. */
export function SetupAtNote() {
  const { say } = useStaffText()
  const server = useSession()?.identity.setupAt
  if (!server) return null
  return (
    <p className="setup-at" role="status">
      {say('admin.setupAt', { server })}
    </p>
  )
}
